import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck, Phone, Mail, MapPin } from "lucide-react";

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", contact_name: "", phone: "", email: "", address: "", notes: "", is_active: true });

  const fetchVendors = useCallback(async () => {
    try {
      const res = await api.get("/vendors");
      setVendors(res.data);
    } catch { toast.error("Failed to load vendors"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", contact_name: "", phone: "", email: "", address: "", notes: "", is_active: true });
    setShowDialog(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({ name: v.name, contact_name: v.contact_name || "", phone: v.phone || "", email: v.email || "", address: v.address || "", notes: v.notes || "", is_active: v.is_active !== false });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing) { await api.put(`/vendors/${editing.vendor_id}`, form); toast.success("Updated"); }
      else { await api.post("/vendors", form); toast.success("Created"); }
      setShowDialog(false); fetchVendors();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try { await api.delete(`/vendors/${id}`); toast.success("Deleted"); fetchVendors(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-6" data-testid="vendors-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="vendors-title">Vendors</h1>
          <p className="text-sm text-slate-500 mt-1">{vendors.length} vendors</p>
        </div>
        <Button onClick={openAdd} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-vendor-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Vendor
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="vendors-list">
        <div className="grid grid-cols-[minmax(0,1fr)_150px_150px_150px_80px_80px] gap-2 px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-200">
          <div>Vendor</div><div>Contact</div><div>Phone</div><div>Email</div><div className="text-center">Active</div><div className="text-right">Actions</div>
        </div>
        {vendors.map((v) => (
          <div key={v.vendor_id} className="grid grid-cols-[minmax(0,1fr)_150px_150px_150px_80px_80px] gap-2 items-center px-4 py-3 border-b border-slate-50 hover:bg-slate-50 group" data-testid={`vendor-row-${v.vendor_id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <Truck className="h-4 w-4 text-[var(--brand-primary)] flex-shrink-0" />
              <span className="text-sm font-medium text-slate-800 truncate">{v.name}</span>
            </div>
            <span className="text-sm text-slate-600 truncate">{v.contact_name || "—"}</span>
            <span className="text-sm text-slate-600 truncate">{v.phone || "—"}</span>
            <span className="text-sm text-slate-600 truncate">{v.email || "—"}</span>
            <div className="text-center">
              <Badge variant={v.is_active ? "default" : "secondary"} className="text-xs">{v.is_active ? "Yes" : "No"}</Badge>
            </div>
            <div className="text-right flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => openEdit(v)} className="p-1 rounded hover:bg-blue-100" data-testid={`edit-vendor-${v.vendor_id}`}><Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" /></button>
              <button onClick={() => remove(v.vendor_id)} className="p-1 rounded hover:bg-red-100" data-testid={`del-vendor-${v.vendor_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
            </div>
          </div>
        ))}
        {vendors.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No vendors yet. Add your first vendor to get started.</div>}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="vendor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Vendor" : "New Vendor"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Manage supplier information</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Vendor Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Sysco Foods" data-testid="vendor-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Contact Name</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="John Smith" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="555-1234" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vendor@email.com" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Notes</Label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" placeholder="Delivery schedule, account #, etc." />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label className="text-sm">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="vendor-save-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
